import { Trans, t } from '@lingui/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import React from 'react';
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  type CollisionDetection,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { MessageDescriptor } from '@lingui/core';
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuAction,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenuBadge,
  SidebarSeparator,
  useSidebar,
} from '@shared/ui/sidebar';
import { Avatar, AvatarFallback, AvatarImage } from '@shared/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@shared/ui/dropdown-menu';
import { Badge } from '@shared/ui/badge';
import { Settings, LogOut, User, ChevronUp, CloudOff, GripVertical, Star } from 'lucide-react';
import type { UserProfile } from '@shared/types/auth';
import {
  getRoleHomePath,
  reorderNavGroup,
  type NavGroupId,
  type NavItem,
} from '@shared/lib/navigation';
import { cn } from '@shared/lib/utils';
import { localizePath, stripLocaleFromPathname, useLocale } from '@shared/i18n';
import { useAuth } from '@shared/hooks/useAuth';
import { useDoctorWorkspace } from '@shared/hooks/useDoctorWorkspace';
import { useIntentPrefetch } from '@shared/hooks/useIntentPrefetch';
import { useNavPrefetch } from './useNavPrefetch';
import { useSidebarPreferences } from './useSidebarPreferences';

interface AppSidebarProps {
  user: UserProfile;
  navItems: NavItem[];
  roleOverride?: UserProfile['role'];
  disableNavPrefetch?: boolean;
}

const NAV_GROUP_LABELS: Record<NavGroupId, MessageDescriptor> = {
  overview: msg({ id: 'shared.layout.AppSidebar.group.overview', message: 'Overview' }),
  'patients-sales': msg({
    id: 'shared.layout.AppSidebar.group.patients-sales',
    message: 'Patients & Sales',
  }),
  'care-logistics': msg({
    id: 'shared.layout.AppSidebar.group.care-logistics',
    message: 'Care & Logistics',
  }),
  money: msg({ id: 'shared.layout.AppSidebar.group.money', message: 'Money' }),
  platform: msg({ id: 'shared.layout.AppSidebar.group.platform', message: 'Platform' }),
};
const DOCTOR_PATIENTS_LABEL = msg({
  id: 'shared.layout.AppSidebar.group.patients-care',
  message: 'Patients & Care',
});

const formatBadgeCount = (count: number): string => (count > 99 ? '99+' : String(count));

// The group under the pointer is the drop target; keyboard drags have no pointer.
const groupCollision: CollisionDetection = args => {
  const hits = pointerWithin(args);
  return hits.length > 0 ? hits : closestCenter(args);
};

const SortableNavGroup: React.FC<{
  id: NavGroupId;
  label: string;
  handleLabel: string;
  children: React.ReactNode;
}> = ({ id, label, handleLabel, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    isDragging,
    isOver,
    index,
    activeIndex,
  } = useSortable({ id });

  // Groups stay put while one is dragged: a line marks where it will land, so
  // sections of very different heights never slide around under the pointer.
  return (
    <SidebarGroup
      ref={setNodeRef}
      className={cn('group/nav-group transition-opacity', isDragging && 'opacity-40')}
    >
      {isOver && !isDragging && (
        <div
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-x-2 h-0.5 rounded-full bg-sidebar-primary',
            index > activeIndex ? '-bottom-[5px]' : '-top-[5px]'
          )}
        />
      )}
      <SidebarGroupLabel>{label}</SidebarGroupLabel>
      <button
        ref={setActivatorNodeRef}
        type='button'
        aria-label={handleLabel}
        className='absolute right-2 top-3.5 flex size-5 cursor-grab touch-none items-center justify-center rounded text-muted-foreground opacity-0 outline-hidden ring-sidebar-ring transition-opacity duration-150 hover:text-sidebar-foreground focus-visible:opacity-100 focus-visible:ring-2 active:cursor-grabbing group-hover/nav-group:opacity-100 group-data-[collapsible=icon]:hidden [@media(hover:none)]:opacity-100'
        {...attributes}
        {...listeners}
      >
        <GripVertical className='size-3.5' />
      </button>
      {children}
    </SidebarGroup>
  );
};

const getRoleDisplayName = (
  user: UserProfile,
  translate: (descriptor: MessageDescriptor) => string
): string => {
  if (user.role === 'doctor' && user.organization_roles?.includes('owner')) {
    return translate(
      msg({
        id: 'shared.layout.AppSidebar.role.organization-owner',
        message: 'Organization Owner',
      })
    );
  }

  switch (user.role) {
    case 'patient':
      return translate(
        msg({
          id: 'shared.layout.AppSidebar.role.patient',
          message: 'Patient',
        })
      );
    case 'doctor':
      return translate(
        msg({
          id: 'shared.layout.AppSidebar.role.doctor',
          message: 'Doctor',
        })
      );
    case 'admin':
      return translate(
        msg({
          id: 'shared.layout.AppSidebar.role.administrator',
          message: 'Administrator',
        })
      );
    case 'super_admin':
      return translate(
        msg({
          id: 'shared.layout.AppSidebar.role.super-admin',
          message: 'Super Admin',
        })
      );
    default:
      return translate(
        msg({
          id: 'shared.layout.AppSidebar.role.user',
          message: 'User',
        })
      );
  }
};

export const AppSidebar: React.FC<AppSidebarProps> = ({
  user,
  navItems,
  roleOverride,
  disableNavPrefetch = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { i18n } = useLingui();
  const { locale } = useLocale();
  const { getIntentHandlers } = useIntentPrefetch();
  const { prefetchPath } = useNavPrefetch();
  const { resolveDoctorPath, isEffectiveOrganizationOwner } = useDoctorWorkspace();
  const { isMobile, setOpenMobile } = useSidebar();
  const normalizedPathname = React.useMemo(
    () => stripLocaleFromPathname(location.pathname),
    [location.pathname]
  );
  const navRole = roleOverride ?? user.role;
  const isStaff = navRole === 'admin' || navRole === 'super_admin' || navRole === 'doctor';
  const { preferences, edit, syncError, retry } = useSidebarPreferences(isStaff, user.id, navRole);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const [draggedGroup, setDraggedGroup] = React.useState<NavGroupId | null>(null);
  const groupLabel = (id: NavGroupId): string =>
    i18n._(
      navRole === 'doctor' && id === 'patients-sales' ? DOCTOR_PATIENTS_LABEL : NAV_GROUP_LABELS[id]
    );
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setDraggedGroup(null);
    if (!over) return;
    const next = reorderNavGroup(
      preferences.groupOrder,
      String(active.id) as NavGroupId,
      String(over.id) as NavGroupId
    );
    if (next !== preferences.groupOrder) edit(() => ({ groupOrder: next }));
  };

  const toggleFavorite = (key: string) =>
    edit(current => ({
      favorites: current.favorites.includes(key)
        ? current.favorites.filter(favorite => favorite !== key)
        : [...current.favorites, key],
    }));

  const renderItems = (items: NavItem[], sectionKey: string) =>
    items.map(item => {
      // Check for exact match or if pathname starts with item path followed by a slash.
      const isActive =
        normalizedPathname === item.path || normalizedPathname.startsWith(item.path + '/');
      const Icon = item.icon;
      const localizedItemPath = localizePath(item.path, locale);
      const intentHandlers = disableNavPrefetch
        ? undefined
        : getIntentHandlers(item.path, () => {
            void prefetchPath({
              path: item.path,
              role: roleOverride ?? user.role,
            });
          });

      const content = (
        <SidebarMenuButton
          asChild
          isActive={isActive}
          // The star sits on the row, so the row stays highlighted while it is hovered.
          className={
            isStaff
              ? 'group-hover/menu-item:bg-sidebar-accent group-hover/menu-item:text-sidebar-accent-foreground'
              : undefined
          }
          tooltip={
            item.description
              ? typeof item.description === 'string'
                ? item.description
                : i18n._(item.description as MessageDescriptor)
              : undefined
          }
        >
          <Link
            to={localizedItemPath}
            className='flex items-center gap-2'
            // Links are not reorderable; stop the browser's own link-drag ghost.
            draggable={false}
            onClick={handleNavClick}
            {...intentHandlers}
          >
            <Icon />
            <span>
              {typeof item.label === 'string'
                ? item.label
                : i18n._(item.label as MessageDescriptor)}
            </span>
            {typeof item.badge === 'number' && item.badge > 0 && (
              <SidebarMenuBadge
                className={`h-5 min-w-5 rounded-full bg-sidebar-primary px-1.5 text-sidebar-primary-foreground ${isStaff ? 'right-7' : ''}`}
              >
                {formatBadgeCount(item.badge)}
              </SidebarMenuBadge>
            )}
          </Link>
        </SidebarMenuButton>
      );
      const favorite = preferences.favorites.includes(item.key);
      return (
        <SidebarMenuItem key={`${sectionKey}-${item.key}`}>
          {content}
          {isStaff && (
            <SidebarMenuAction
              type='button'
              aria-label={t({
  id: 'shared.layout.AppSidebar.value0-value1',
  message: `${favorite ? t({
    id: 'shared.layout.AppSidebar.remove-favorite',
    message: 'Remove from favorites'
  }) : t({
    id: 'shared.layout.AppSidebar.add-favorite',
    message: 'Add to favorites'
  })}: ${typeof item.label === 'string' ? item.label : i18n._(item.label)}`
})}
              onClick={() => toggleFavorite(item.key)}
              className={cn(
                'transition-opacity duration-150 hover:bg-transparent group-hover/menu-item:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground [&>svg]:size-3.5 [&>svg]:opacity-45 [&>svg]:transition-opacity hover:[&>svg]:opacity-100 focus-visible:[&>svg]:opacity-100',
                // A quiet filled star marks a favorite in its own group; elsewhere it waits for hover.
                (!favorite || sectionKey === 'favorites') &&
                  'opacity-0 focus-visible:opacity-100 group-hover/menu-item:opacity-100 [@media(hover:none)]:opacity-100'
              )}
            >
              <Star className={favorite ? 'fill-current' : undefined} />
            </SidebarMenuAction>
          )}
        </SidebarMenuItem>
      );
    });

  // Staff roles get grouped sections; everyone else keeps one flat list.
  const navSections = React.useMemo(() => {
    if (!isStaff) {
      return [{ id: undefined as NavGroupId | undefined, items: navItems }];
    }
    return preferences.groupOrder
      .map(id => ({
        id: id as NavGroupId | undefined,
        items: navItems.filter(item => (item.group ?? 'platform') === id),
      }))
      .filter(section => section.items.length > 0);
  }, [isStaff, navItems, preferences.groupOrder]);
  const favoriteItems = preferences.favorites
    .map(key => navItems.find(item => item.key === key))
    .filter((item): item is NavItem => Boolean(item));

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  const handleProfileClick = () => {
    navigate(localizePath(resolveDoctorPath('/doctor-profile'), locale));
  };

  const handleNavClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const getUserInitials = (name: string): string => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
      return t({
        id: 'shared.layout.AppSidebar.initials-fallback-dr',
        message: 'DR',
      });
    }
    return (
      parts
        .slice(0, 2)
        .map(part => part.charAt(0).toUpperCase())
        .join('') ||
      t({
        id: 'shared.layout.AppSidebar.initials-fallback-dr',
        message: 'DR',
      })
    );
  };

  const roleDisplay = getRoleDisplayName(user, descriptor => i18n._(descriptor));
  const canViewDoctorProfile =
    user.role === 'doctor' ||
    user.role === 'org_owner' ||
    user.organization_roles?.includes('owner') === true ||
    isEffectiveOrganizationOwner;
  const homePath = localizePath(
    resolveDoctorPath(getRoleHomePath(roleOverride ?? user.role)),
    locale
  );
  const displayName = `${user.first_name} ${user.last_name}`.trim() || user.email;
  const headline = undefined;
  const profileEmail = user.email;
  const fallbackAvatar = `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(displayName)}`;
  const avatarSrc = fallbackAvatar;
  const userInitials = getUserInitials(displayName);

  return (
    <Sidebar collapsible='icon' variant='inset'>
      <SidebarHeader className='group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:px-0.5 group-data-[collapsible=icon]:py-1.5'>
        <SidebarMenu className='group-data-[collapsible=icon]:items-center'>
          <SidebarMenuItem className='group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:justify-center'>
            <SidebarMenuButton
              size='lg'
              asChild
              className='justify-center hover:bg-transparent hover:text-sidebar-foreground active:bg-transparent data-[state=open]:hover:bg-transparent data-[slot=sidebar-menu-button]:!p-1.5 group-data-[collapsible=icon]:!size-11 group-data-[collapsible=icon]:!p-0'
            >
              <Link to={homePath} className='flex size-full items-center justify-center'>
                <img
                  src='/Icon_Jema_full.svg'
                  alt={t({
                    id: 'shared.layout.AppSidebar.jema-logo',
                    message: 'Jema Logo',
                  })}
                  className='hidden size-10 object-contain group-data-[collapsible=icon]:block'
                />
                <img
                  src='/Icon_Jema_full.svg'
                  alt={t({
                    id: 'shared.layout.AppSidebar.jema-logo',
                    message: 'Jema Logo',
                  })}
                  className='h-10 max-w-[10rem] object-contain group-data-[collapsible=icon]:hidden'
                />
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        {isStaff && (
          <SidebarGroup>
            <SidebarGroupLabel>
              <Trans id='shared.layout.AppSidebar.favorites'>Favorites</Trans>
            </SidebarGroupLabel>
            <SidebarGroupContent>
              {favoriteItems.length > 0 ? (
                <SidebarMenu>{renderItems(favoriteItems, 'favorites')}</SidebarMenu>
              ) : (
                <p className='px-2 text-xs text-muted-foreground group-data-[collapsible=icon]:hidden'>
                  <Trans id='shared.layout.AppSidebar.favorites-hint'>
                    Star a link to keep it here.
                  </Trans>
                </p>
              )}
            </SidebarGroupContent>
          </SidebarGroup>
        )}
        {isStaff && <SidebarSeparator />}
        {isStaff ? (
          <DndContext
            sensors={sensors}
            collisionDetection={groupCollision}
            modifiers={[restrictToVerticalAxis]}
            onDragStart={({ active }) => setDraggedGroup(String(active.id) as NavGroupId)}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setDraggedGroup(null)}
          >
            <SortableContext
              items={navSections.map(section => section.id as NavGroupId)}
              strategy={verticalListSortingStrategy}
            >
              {navSections.map(section => {
                const id = section.id as NavGroupId;
                const label = groupLabel(id);
                return (
                  <SortableNavGroup
                    key={id}
                    id={id}
                    label={label}
                    handleLabel={`${t({ id: 'shared.layout.AppSidebar.reorder-group', message: 'Reorder group' })}: ${label}`}
                  >
                    <SidebarGroupContent>
                      <SidebarMenu>{renderItems(section.items, id)}</SidebarMenu>
                    </SidebarGroupContent>
                  </SortableNavGroup>
                );
              })}
            </SortableContext>
            <DragOverlay>
              {draggedGroup && (
                <div className='m-2 flex h-8 cursor-grabbing items-center justify-between rounded-md bg-sidebar px-2 text-xs font-semibold text-sidebar-foreground shadow-lg dark:ring-1 dark:ring-sidebar-border'>
                  {groupLabel(draggedGroup)}
                  <GripVertical className='size-3.5 text-muted-foreground' />
                </div>
              )}
            </DragOverlay>
          </DndContext>
        ) : (
          navSections.map(section => (
            <SidebarGroup key='navigation'>
              <SidebarGroupLabel>
                <Trans id='shared.layout.AppSidebar.navigation'>Navigation</Trans>
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>{renderItems(section.items, 'navigation')}</SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))
        )}
      </SidebarContent>
      <SidebarFooter>
        {isStaff && syncError && (
          <div
            role='status'
            className='flex items-center gap-2 px-2 text-xs group-data-[collapsible=icon]:hidden'
          >
            <CloudOff className='size-3.5 shrink-0 text-destructive' aria-hidden />
            <span className='flex-1 truncate'>
              <Trans id='shared.layout.AppSidebar.sync-error'>Sidebar not synced</Trans>
            </span>
            <button
              type='button'
              onClick={() => void retry()}
              className='rounded font-semibold underline-offset-2 outline-hidden ring-sidebar-ring hover:underline focus-visible:ring-2'
            >
              <Trans id='shared.layout.AppSidebar.sync-retry'>Retry</Trans>
            </button>
          </div>
        )}
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size='lg'
                  className='data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'
                >
                  <Avatar className='size-8 rounded-lg'>
                    <AvatarImage src={avatarSrc} alt={displayName} />
                    <AvatarFallback className='rounded-lg'>{userInitials}</AvatarFallback>
                  </Avatar>
                  <div className='grid flex-1 text-left text-sm leading-tight'>
                    <span className='truncate font-medium'>{displayName}</span>
                    <span className='truncate text-xs text-muted-foreground'>
                      {headline || profileEmail}
                    </span>
                  </div>
                  <ChevronUp className='ml-auto' />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className='w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg'
                side='bottom'
                align='end'
                sideOffset={4}
              >
                <DropdownMenuLabel className='p-0 font-normal'>
                  <div className='flex items-center gap-2 px-1 py-1.5 text-left text-sm'>
                    <Avatar className='h-8 w-8 rounded-lg'>
                      <AvatarImage src={avatarSrc} alt={displayName} />
                      <AvatarFallback className='rounded-lg'>{userInitials}</AvatarFallback>
                    </Avatar>
                    <div className='grid flex-1 gap-1 text-sm'>
                      <span className='truncate font-semibold'>{displayName}</span>
                      <span className='truncate text-xs text-muted-foreground'>{profileEmail}</span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div className='px-1 py-1.5'>
                  <Badge variant='secondary' className='text-xs'>
                    {roleDisplay}
                  </Badge>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem className='gap-2'>
                  <Settings />
                  <Trans id='shared.layout.AppSidebar.settings'>Settings</Trans>
                </DropdownMenuItem>
                {canViewDoctorProfile && (
                  <DropdownMenuItem className='gap-2' onClick={handleProfileClick}>
                    <User />
                    <Trans id='shared.layout.AppSidebar.profile'>Profile</Trans>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className='gap-2 text-red-600 focus:text-red-600 dark:text-red-400 dark:focus:text-red-400'
                  onClick={handleSignOut}
                >
                  <LogOut />
                  <Trans id='shared.layout.AppSidebar.sign-out'>Sign out</Trans>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
};
